import { useQuery } from '@tanstack/react-query'
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import { apiClient } from '../../api'
import { FEATURE_DEFAULTS, FeatureId } from '../client/features.gen'

/**
 * Runtime feature flags.
 *
 * Values come from `GET /api/features` (the DB-backed truth, see
 * docs/plans/DVG-438-feature-flags.md). The catalog defaults (features.gen.ts)
 * are only the offline fallback when the backend cannot be reached.
 *
 * A background poll of `GET /api/features/version` hard-reloads the page when
 * a reshaped admin toggles anything, so every open client converges on the
 * new configuration (and a switched-off broken feature disappears everywhere).
 */

export const FEATURES_QUERY_KEY = ['features'] as const
export const FEATURES_VERSION_QUERY_KEY = ['features', 'version'] as const

const VERSION_POLL_INTERVAL_MS = 10_000
const FALLBACK_RETRY_INTERVAL_MS = 30_000
const RELOAD_GRACE_MS = 1_500

let reloadScheduled = false

/**
 * Hard-reload this tab after a short toast. Idempotent: the poll and the admin
 * page may both ask for it. Clears the persisted react-query cache first so
 * nothing from the previous configuration rehydrates.
 */
export function reloadForNewConfiguration(message = 'Configuration changed, reloading…') {
  if (reloadScheduled) return
  reloadScheduled = true
  toast.info(message, { toastId: 'features-reload', autoClose: RELOAD_GRACE_MS })
  window.setTimeout(() => {
    try {
      window.localStorage.removeItem('react-query-cache')
    } catch {
      // storage unavailable; the reload still refetches everything
    }
    window.location.reload()
  }, RELOAD_GRACE_MS)
}

// Catalog ids only. Per-source flags (`knowledge_source.<key>`) are not
// exposed on the public read; `/api/knowledge/visible-sources` decides per user.
type FeatureMap = Partial<Record<FeatureId, boolean>>

interface FeaturesContextType {
  features: FeatureMap
  /** Global feature version; `null` while running on the build-time fallback. */
  version: number | null
  isEnabled: (feature: FeatureId) => boolean
}

const FeaturesContext = createContext<FeaturesContextType | undefined>(undefined)

const offlineFallback: FeatureMap = FEATURE_DEFAULTS

export const useFeatures = (): FeaturesContextType => {
  const context = useContext(FeaturesContext)
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeaturesProvider')
  }
  return context
}

/** Convenience: `const showAgents = useFeature(Feature.AGENTS)`. */
export const useFeature = (feature: FeatureId): boolean => useFeatures().isEnabled(feature)

/**
 * Poll the global version and reload once it differs from the one this page
 * loaded with. Disabled while on the fallback (nothing to compare against).
 */
function useFeatureVersionPoll(loadedVersion: number | null) {
  const { data } = useQuery({
    queryKey: FEATURES_VERSION_QUERY_KEY,
    queryFn: apiClient.features.fetchVersion,
    enabled: loadedVersion !== null,
    staleTime: 0,
    refetchInterval: VERSION_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  })
  useEffect(() => {
    if (loadedVersion !== null && data && data.version !== loadedVersion) {
      reloadForNewConfiguration()
    }
  }, [data, loadedVersion])
}

export const FeaturesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data, isPending, isError } = useQuery({
    queryKey: FEATURES_QUERY_KEY,
    queryFn: apiClient.features.fetchFeatures,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    // On the offline fallback keep trying, so the version poll comes alive as
    // soon as the backend is reachable again.
    refetchInterval: query => (query.state.data ? false : FALLBACK_RETRY_INTERVAL_MS),
  })

  const features = data?.features ?? offlineFallback
  const version = data?.version ?? null

  useFeatureVersionPoll(version)

  useEffect(() => {
    if (isError) {
      console.warn('Feature flags unavailable; using catalog defaults', offlineFallback)
    }
  }, [isError])

  const isEnabled = useCallback((feature: FeatureId) => features[feature] === true, [features])

  const value = useMemo<FeaturesContextType>(
    () => ({ features, version, isEnabled }),
    [features, version, isEnabled],
  )

  // Block first paint until the fetch settles (success or error): rendering
  // the fallback first would flash gated UI in and out.
  if (isPending) {
    return null
  }

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>
}
